import React from 'react';
import { Helmet } from 'react-helmet';

const SEOHead = ({ 
  title = "Nand Blogs - Salesforce Expert Insights & Best Practices",
  description = "Your ultimate Salesforce resource hub. Expert insights, best practices, tutorials, and success stories for Salesforce developers, administrators, and business users. Learn Apex, LWC, Flow, integrations, and more.",
  keywords = "Salesforce, Salesforce development, Apex programming, Lightning Web Components, LWC, Salesforce Flow, Salesforce integration, Salesforce tutorials, Salesforce best practices, Salesforce admin, CRM, Salesforce API, Salesforce automation, Salesforce customization, Salesforce training, Salesforce consulting, Salesforce solutions, Salesforce tips, Salesforce tricks, Salesforce guides, Salesforce documentation",
  image = "https://nandblogs.com/favicon.png",
  url = "https://nandblogs.com/",
  type = "website",
  author = "Nand Blogs",
  publishedTime = null,
  modifiedTime = null,
  section = "Technology",
  tags = ["Salesforce", "CRM", "Development"],
  structuredData = null
}) => {
  const fullTitle = title.includes("Nand Blogs") ? title : `${title} | Nand Blogs`;
  const fullUrl = url.startsWith("https://") ? url : `https://nandblogs.com${url}`;
  const fullImage = image.startsWith("https://") ? image : `https://nandblogs.com${image}`;

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": type === "article" ? "Article" : "WebPage",
    "headline": title,
    "description": description,
    "image": fullImage,
    "url": fullUrl,
    "author": {
      "@type": "Person",
      "name": author,
      "url": "https://nandblogs.com/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Nand Blogs",
      "url": "https://nandblogs.com/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nandblogs.com/favicon.png"
      }
    },
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Nand Blogs" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      
      {/* Article specific tags */}
      {type === "article" && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:publisher" content="https://nandblogs.com/" />
          <meta property="article:section" content={section} />
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@NandBlogs" />
      <meta name="twitter:creator" content="@NandKishor" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:domain" content="nandblogs.com" />
      <meta name="twitter:url" content={fullUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      {/* Apollo Tracking Script */}
      <script>
        {`
          function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");
          o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,
          o.onload=function(){window.trackingFunctions.onLoad({appId:"67ef84d35ff339001db5ffa8"})},
          document.head.appendChild(o)}initApollo();
        `}
      </script>



    </Helmet>
  );
};

export default SEOHead;
